import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";

export function Card({
  children,
  className,
  flat = false,
}: {
  children: React.ReactNode;
  className?: string;
  flat?: boolean;
}) {
  return (
    <div
      className={cn(
        flat
          ? "rounded-3xl bg-canvas"
          : "rounded-3xl bg-surface shadow-[var(--shadow-elevation)]",
        className
      )}
    >
      {children}
    </div>
  );
}

export function MetricCard({
  label,
  value,
  detail,
  trend,
  className,
  flat = true,
  info,
}: {
  label: string;
  value: string;
  detail?: string;
  trend?: "up" | "down" | "neutral";
  className?: string;
  flat?: boolean;
  info?: { title: string; description: string };
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  void flat;

  return (
    <>
      <div
        className={cn(
          "animate-rise relative overflow-hidden rounded-3xl border border-white/70 bg-gradient-to-br from-white to-canvas/60 p-5 shadow-[var(--shadow-elevation)] transition-shadow duration-300 hover:shadow-[var(--shadow-elevation-lg)]",
          className
        )}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-accent/15 blur-2xl"
        />
        <div className="flex items-center gap-1.5">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-accent" />
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted">{label}</p>
          {info ? (
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              className="flex h-5 w-5 items-center justify-center rounded-full text-accent transition-colors hover:bg-accent-subtle hover:text-accent-hover focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label={`More about ${label}`}
            >
              <Info className="h-3.5 w-3.5" strokeWidth={2.25} />
            </button>
          ) : null}
        </div>
        <p className="mt-3 text-[2.25rem] font-extrabold leading-none tracking-[-0.03em] text-foreground">
          {value}
        </p>
        {detail ? (
          <p
            className={cn(
              "mt-2 text-xs font-semibold",
              trend === "up" && "text-accent-link",
              trend === "down" && "text-fuel-high",
              !trend && "text-muted"
            )}
          >
            {detail}
          </p>
        ) : null}
      </div>

      {info ? (
        <Modal open={infoOpen} onClose={() => setInfoOpen(false)} title={info.title}>
          {info.description}
        </Modal>
      ) : null}
    </>
  );
}

export function FeatureTile({
  href,
  icon,
  label,
  meta,
  iconClassName,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  meta?: string;
  iconClassName?: string;
}) {
  return (
    <Link to={href} className="group feature-tile">
      <div className={cn("feature-tile-icon", iconClassName)}>{icon}</div>
      <div>
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {meta ? <p className="mt-0.5 text-xs font-medium text-muted">{meta}</p> : null}
      </div>
    </Link>
  );
}

export function QuickAction({
  href,
  icon,
  title,
  description,
  onClick,
}: {
  href?: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface text-foreground shadow-[var(--shadow-elevation)] transition-transform duration-300 ease-[var(--ease-out-expo)] group-hover:scale-110 group-hover:bg-accent-subtle">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted">{description}</p>
      </div>
      <span className="shrink-0 text-sm text-muted transition-transform duration-300 ease-[var(--ease-out-expo)] group-hover:translate-x-1 group-hover:text-foreground">
        →
      </span>
    </>
  );

  const className =
    "group hover-raise flex w-full items-center gap-3 rounded-3xl bg-canvas p-4 text-left hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

  if (href) {
    return (
      <Link to={href} className={className}>
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
}

export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-xl font-bold tracking-[-0.02em] text-foreground">{title}</h2>
        {description ? <p className="mt-1 text-sm font-medium text-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function AnnouncementPill({
  children,
  href,
}: {
  children: React.ReactNode;
  href?: string;
}) {
  const className = "announcement-pill";
  if (href) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return <div className={className}>{children}</div>;
}
